// client/src/utils/archiveScan.js
// Scan a business-card photo into a draft (no crop/rotate step).
import {
  scanBusinessCard,
  uploadAndExtract
} from '../api/archive';
import { extractDominantColor } from './colorExtractor';
import { pickReadableTextColors } from './contrast';
import { computeClientFallbackThemeFromBlob } from './clientThemeFallback';
import { createDerivedImagesFromBlob } from './businessCardImagePipeline';

export const EMPTY_ARCHIVE_FIELDS = {
  name: '',
  email: '',
  phone: '',
  company: '',
  jobTitle: '',
  website: '',
  address: '',
  notes: ''
};

/**
 * Run OCR + theme extraction on a full photo blob/file.
 * @param {Blob|File} imageBlob
 * @returns {Promise<{
 *   fields: object,
 *   theme: object|null,
 *   primaryColor: string|null,
 *   imageUrl: string|null,
 *   s3Key: string|null,
 *   fieldConfidence: object,
 *   ocrLines: array,
 *   offlineOcrBlob: Blob|null
 * }>}
 */
export async function scanImageToDraft(imageBlob) {
  const { ocrBlob, colorBlob } = await createDerivedImagesFromBlob(imageBlob);
  const ocrFile = new File([ocrBlob], 'business-card-ocr.jpg', { type: ocrBlob.type || 'image/jpeg' });
  const colorFile = new File([colorBlob], 'business-card-color.jpg', { type: colorBlob.type || 'image/jpeg' });

  let scanResult = null;
  try {
    scanResult = await scanBusinessCard({
      imageFile: ocrFile,
      colorImageFile: colorFile,
      saveImage: true
    });
  } catch (scanErr) {
    console.warn('Enhanced scan failed, falling back to legacy OCR:', scanErr);
  }

  if (scanResult) {
    let bg = scanResult.theme?.primaryBackground || scanResult.colors?.primaryBackground || null;
    let derivedTheme = scanResult.theme?.primaryBackground
      ? scanResult.theme
      : (bg ? { primaryBackground: bg, ...pickReadableTextColors(bg) } : null);

    if (!bg) {
      try {
        const fallbackTheme = await computeClientFallbackThemeFromBlob(colorBlob || ocrBlob || imageBlob);
        bg = fallbackTheme?.primaryBackground || null;
        derivedTheme = fallbackTheme || null;
      } catch {
        // non-critical
      }
    }

    return {
      fields: { ...EMPTY_ARCHIVE_FIELDS, ...(scanResult.fields || {}) },
      theme: derivedTheme,
      primaryColor: bg,
      imageUrl: scanResult.imageUrl || null,
      s3Key: scanResult.s3Key || null,
      fieldConfidence: scanResult.fieldConfidence || {},
      ocrLines: scanResult.ocr?.lines || [],
      offlineOcrBlob: null
    };
  }

  try {
    const extractedColor = await extractDominantColor(ocrFile);
    const legacy = await uploadAndExtract(ocrFile);
    return {
      fields: { ...EMPTY_ARCHIVE_FIELDS, ...(legacy.fields || {}) },
      theme: extractedColor
        ? { primaryBackground: extractedColor, ...pickReadableTextColors(extractedColor) }
        : null,
      primaryColor: extractedColor || null,
      imageUrl: legacy.imageUrl || null,
      s3Key: legacy.s3Key || null,
      fieldConfidence: {},
      ocrLines: [],
      offlineOcrBlob: null
    };
  } catch (legacyErr) {
    console.error('Legacy OCR failed:', legacyErr);
    return {
      fields: { ...EMPTY_ARCHIVE_FIELDS },
      theme: null,
      primaryColor: null,
      imageUrl: null,
      s3Key: null,
      fieldConfidence: {},
      ocrLines: [],
      offlineOcrBlob: imageBlob
    };
  }
}
